import React, { useState, useEffect } from 'react';
import {
  readCodeFromHash,
  buildScreens,
  answeredCount,
  screenComplete,
  firstIncompleteScreen,
  saveProgress,
  loadProgress,
  clearProgress,
  buildSubmitBody,
  submitErrorMessage,
  noticeAvailability,
} from '../lib/psicosocial-formulario';
import { Answers } from '../lib/psicosocial-calculo';
import { PublicNotice } from '../lib/psicosocial-envio';
import { buildNoticeSections, NOTICE_VERSION } from '../lib/psicosocial-aviso';
import { PSICOSOCIAL_RESPONSE_SCALE } from '../lib/psicosocial-instrumento';
import { PSICOSOCIAL_ITEMS } from '../lib/psicosocial-items';
import EnlaceNoDisponible from '../components/EnlaceNoDisponible';

const FUNCTION_URL = 'https://uhwuhqhfutvgitgtrxej.supabase.co/functions/v1/psicosocial-submit';

// Pantallas del cuestionario (una por dimensión/bloque, sin nombre de
// dimensión) — estáticas, no dependen de props ni de datos del servidor.
const SCREENS = buildScreens();

// Lookup n -> texto del ítem, para no recorrer el arreglo 58 veces por render.
const ITEM_TEXT: Record<number, string> = {};
for (let i = 0; i < PSICOSOCIAL_ITEMS.length; i++) {
  ITEM_TEXT[PSICOSOCIAL_ITEMS[i].n] = PSICOSOCIAL_ITEMS[i].text;
}

function formatDueAt(dueAt: string | null): string {
  if (dueAt === null) return '';
  const d = new Date(dueAt);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es-EC', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function PsicosocialPublicPage({ assessmentId }: { assessmentId: string }) {
  // El código vive SOLO en memoria del componente (viene del fragmento de
  // la URL, #c=...) — nunca se registra en consola ni se guarda en storage.
  const [code] = useState<string | null>(() => readCodeFromHash(window.location.hash));

  const [loadingNotice, setLoadingNotice] = useState(true);
  const [notice, setNotice] = useState<PublicNotice | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  // Progreso guardado (si hay uno vigente, dentro de PROGRESS_TTL_HOURS) se
  // recupera de forma síncrona al montar, sin esperar al notice.
  const [restored] = useState(() => (code ? loadProgress(window.localStorage, assessmentId, new Date()) : null));
  const [answers, setAnswers] = useState<Answers>(() => restored?.answers ?? {});
  const [screenIndex, setScreenIndex] = useState<number>(() => restored?.screenIndex ?? 0);
  // Si ya había progreso guardado, el trabajador ya vio el aviso de
  // privacidad la primera vez — no se lo repetimos.
  const [privacyAcknowledged, setPrivacyAcknowledged] = useState<boolean>(() => !!restored);
  // Casilla "He leído el aviso": habilita el botón para empezar.
  const [noticeRead, setNoticeRead] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    (async () => {
      setLoadingNotice(true);
      setLoadError(null);
      try {
        const res = await fetch(`${FUNCTION_URL}?id=${assessmentId}`);
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok || data.ok !== true) {
          if (res.status === 404) setNotFound(true);
          else setLoadError(submitErrorMessage(res.status, data.error ?? null));
        } else {
          setNotice(data.notice);
        }
      } catch {
        if (!cancelled) setLoadError(submitErrorMessage(null, null));
      } finally {
        if (!cancelled) setLoadingNotice(false);
      }
    })();
    return () => { cancelled = true; };
  }, [assessmentId, code]);

  // Autoguardado de progreso (nunca del código) mientras haya código válido
  // y todavía no se haya enviado el cuestionario.
  useEffect(() => {
    if (!code || submitted) return;
    saveProgress(window.localStorage, assessmentId, { answers, screenIndex }, new Date());
  }, [code, submitted, assessmentId, answers, screenIndex]);

  function setAnswer(itemNo: number, value: number) {
    setAnswers(prev => ({ ...prev, [itemNo]: value }));
  }

  async function handleSubmit() {
    if (!code) return;
    const incompleteIdx = firstIncompleteScreen(SCREENS, answers);
    if (incompleteIdx !== -1) {
      setScreenIndex(incompleteIdx);
      setSubmitError('Todavía hay preguntas sin responder.');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    let status: number | null = null;
    let errorCode: string | null = null;
    try {
      const body = buildSubmitBody(code, answers);
      const res = await fetch(FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      status = res.status;
      const data = await res.json();
      if (!res.ok || data.ok !== true) {
        errorCode = data.error ?? null;
        throw new Error('submit_failed');
      }
      clearProgress(window.localStorage, assessmentId);
      setSubmitted(true);
    } catch {
      setSubmitError(submitErrorMessage(status, errorCode));
    } finally {
      setSubmitting(false);
    }
  }

  // ── Enlace inválido o incompleto: ni siquiera se intenta cargar el notice ──
  if (!code) {
    return <EnlaceNoDisponible message="El enlace está incompleto: falta el código de acceso. Ábralo de nuevo desde el mensaje o el código QR que recibió." />;
  }

  if (loadingNotice) {
    return <div style={wrap}><p style={muted}>Cargando…</p></div>;
  }

  if (notFound) {
    return <EnlaceNoDisponible message="Esta evaluación no existe o ya fue eliminada. Consulte con el responsable de Seguridad y Salud de su empresa." />;
  }

  if (loadError) {
    return (
      <div style={wrap}>
        <div style={card}>
          <p style={{ color: '#ef4444' }}>{loadError}</p>
        </div>
      </div>
    );
  }

  if (!notice) return null;

  const availability = noticeAvailability(notice, new Date());

  if (availability !== 'disponible') {
    const dueAtText = formatDueAt(notice.due_at);
    return (
      <EnlaceNoDisponible
        title={notice.title}
        subtitle={notice.company_name}
        message={availability === 'vencida'
          ? `El plazo para responder este cuestionario venció${dueAtText ? ` el ${dueAtText}` : ''}.`
          : 'Esta evaluación no está recibiendo respuestas en este momento.'}
      />
    );
  }

  if (submitted) {
    return (
      <div style={wrap}>
        <div style={card}>
          <h1 style={title}>¡Gracias por responder!</h1>
          <p style={muted}>Sus respuestas se guardaron sin su nombre. Ya puede cerrar esta página.</p>
        </div>
      </div>
    );
  }

  if (!privacyAcknowledged) {
    // Aviso completo (psicosocial-aviso.ts), con los datos reales del GET de la
    // Edge Function. Se lee con el scroll normal de la página (cómodo en celular).
    const sections = buildNoticeSections(notice);
    return (
      <div style={wrap}>
        <div style={card}>
          <h1 style={title}>{notice.title}</h1>
          <p style={muted}>{notice.company_name}</p>
          <h2 style={noticeTitle}>Aviso de privacidad</h2>
          <p style={muted}>Léalo antes de empezar el cuestionario.</p>
          {sections.map(section => (
            <section key={section.heading} style={{ marginTop: 16 }}>
              <h3 style={noticeHeading}>{section.heading}</h3>
              {section.paragraphs.map((paragraph, i) => (
                <p key={i} style={noticeText}>{paragraph}</p>
              ))}
            </section>
          ))}
          <p style={{ ...muted, fontSize: '0.75rem', marginTop: 16 }}>Versión del aviso: {NOTICE_VERSION}</p>
          <label style={{ ...checkLabel, marginTop: 16, padding: '10px 12px', fontSize: '0.9rem' }}>
            <input
              type="checkbox"
              checked={noticeRead}
              onChange={e => setNoticeRead(e.target.checked)}
            />
            He leído el aviso de privacidad
          </label>
          <button
            onClick={() => setPrivacyAcknowledged(true)}
            disabled={!noticeRead}
            style={{ ...submitBtn, marginTop: 16, opacity: noticeRead ? 1 : 0.5, cursor: noticeRead ? 'pointer' : 'not-allowed' }}
          >
            Empezar el cuestionario
          </button>
        </div>
      </div>
    );
  }

  // ── Cuestionario ──────────────────────────────────────────────────────
  const screen = SCREENS[screenIndex];
  const isLastScreen = screenIndex === SCREENS.length - 1;
  const canAdvance = screenComplete(screen, answers);
  const totalItems = SCREENS.reduce((n, s) => n + s.itemNos.length, 0);

  return (
    <div style={wrap}>
      <div style={card}>
        <p style={muted}>
          Pantalla {screenIndex + 1} de {SCREENS.length} — {answeredCount(answers)} de {totalItems} respondidas
        </p>

        {screen.itemNos.map(itemNo => (
          <div key={itemNo} style={{ marginTop: 18, paddingBottom: 14, borderBottom: '1px solid #334155' }}>
            <p style={{ color: '#e2e8f0', lineHeight: 1.5, marginBottom: 10 }}>{ITEM_TEXT[itemNo]}</p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {PSICOSOCIAL_RESPONSE_SCALE.map(opt => (
                <label key={opt.value} style={checkLabel}>
                  <input
                    type="radio"
                    name={`item-${itemNo}`}
                    checked={answers[itemNo] === opt.value}
                    onChange={() => setAnswer(itemNo, opt.value)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
        ))}

        {submitError && <p style={{ color: '#ef4444', marginTop: 14 }}>{submitError}</p>}

        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button
            onClick={() => setScreenIndex(i => Math.max(0, i - 1))}
            disabled={screenIndex === 0 || submitting}
            style={{ ...secondaryBtn, opacity: screenIndex === 0 || submitting ? 0.5 : 1 }}
          >
            Anterior
          </button>
          {isLastScreen ? (
            <button
              onClick={handleSubmit}
              disabled={!canAdvance || submitting}
              style={{ ...submitBtn, flex: 1, opacity: !canAdvance || submitting ? 0.5 : 1 }}
            >
              {submitting ? 'Enviando…' : 'Enviar'}
            </button>
          ) : (
            <button
              onClick={() => setScreenIndex(i => Math.min(SCREENS.length - 1, i + 1))}
              disabled={!canAdvance}
              style={{ ...submitBtn, flex: 1, opacity: !canAdvance ? 0.5 : 1 }}
            >
              Siguiente
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const wrap: React.CSSProperties = { minHeight: '100vh', background: '#0f172a', display: 'flex', justifyContent: 'center', padding: '32px 16px' };
const card: React.CSSProperties = { background: '#1e293b', border: '1px solid #334155', borderRadius: 16, padding: 28, width: '100%', maxWidth: 640 };
const title: React.CSSProperties = { color: '#f1f5f9', fontSize: '1.3rem', fontWeight: 700, marginBottom: 8 };
const muted: React.CSSProperties = { color: '#94a3b8', fontSize: '0.9rem' };
const noticeTitle: React.CSSProperties = { color: '#f1f5f9', fontSize: '1.05rem', fontWeight: 700, marginTop: 20, marginBottom: 4 };
const noticeHeading: React.CSSProperties = { color: '#e2e8f0', fontSize: '0.95rem', fontWeight: 700, marginBottom: 6 };
const noticeText: React.CSSProperties = { color: '#cbd5e1', fontSize: '0.9rem', lineHeight: 1.6, margin: '0 0 8px 0' };
const checkLabel: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, color: '#e2e8f0', fontSize: '0.86rem', border: '1px solid #334155', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' };
const submitBtn: React.CSSProperties = { width: '100%', padding: '12px 24px', background: 'linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%)', border: 'none', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.92rem', fontWeight: 700 };
const secondaryBtn: React.CSSProperties = { padding: '12px 20px', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 8, cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600 };
